/**
 * Firebase 的「不載 SDK 也能呼叫」接縫(R413)。
 * -----------------------------------------------------------------------------
 * Firebase SDK(firestore + auth + re2js + webchannel ≈ 640 KB)原本被三條**靜態** import
 * 拉進主 bundle:`services/index.ts` 的 `onAuthLost`、`lib/clearClientAuthState.ts` 的登出、
 * 以及 `useFirestoreSubscription`。而官網 / 條款 / 登入頁根本不訂閱任何即時資料 → 每個訪客
 * 都在下載、解析一份用不到的 SDK。
 *
 * 現在 SDK 只在**第一次即時訂閱**時由 `useFirestoreSubscription` 動態載入
 * (`firestoreRuntime.ts`)。這支檔案**不 import 任何 firebase 模組**,負責兩件事:
 *
 * 1. **token fetcher 註冊**:`services/index.ts` 開機時註冊;`auth.ts` 在 sign-in 當下才讀。
 * 2. **登出 / onAuthLost 的 Firebase 收尾**:`auth.ts` 載入時登記自己的 signOut / reset;
 *    沒載入過 = 這個分頁從沒 sign in 過 Firebase(persistence 是 `inMemoryPersistence`,
 *    沒有 IndexedDB 殘留)→ 沒有東西要清,直接略過。
 *
 * ⚠️ **不要在這支檔案(或任何主 bundle 會走到的檔)靜態 import `firebase/*` 或 `./init` /
 * `./auth`** —— 那會把整包 SDK 拉回主 bundle,而且零徵兆(功能完全正常,只是變慢)。
 * 守門 = `src/app/mainBundleBoundary.test.ts`(從 `main.tsx` 走靜態 import 圖)。
 *
 * ⚠️ 收尾順序仍由呼叫端決定(ADR-002:detach → signOut → reset cache → 清 store),
 * 這裡只把「SDK 有沒有載入」這個判斷收在一處。
 */

/** BE `POST /api/auth/firebase-token` 解 envelope 後的 `result`(SSOT 第二份 §2)。 */
export interface FirebaseTokenResponse {
  firebase_token: string;
  expires_in: number;
}

export type FirebaseTokenFetcher = () => Promise<FirebaseTokenResponse>;

let tokenFetcher: FirebaseTokenFetcher | null = null;

/** `services/index.ts` 開機時呼一次(已套 cookie + CSRF 的 BE 呼叫)。 */
export function setFirebaseTokenFetcher(fetcher: FirebaseTokenFetcher): void {
  tokenFetcher = fetcher;
}

/** `auth.ts` 在 sign-in 當下讀(不在 module 頂層讀:註冊可能晚於 SDK chunk 載入)。 */
export function getFirebaseTokenFetcher(): FirebaseTokenFetcher | null {
  return tokenFetcher;
}

/** test 用:回到未註冊狀態。production / dev 不要呼。 */
export function resetFirebaseTokenFetcherForTest(): void {
  tokenFetcher = null;
}

/** `auth.ts` 載入時登記的收尾動作。 */
export interface LoadedFirebaseAuth {
  signOut(): Promise<void>;
  resetSignInCache(): void;
}

let loadedAuth: LoadedFirebaseAuth | null = null;

/** `auth.ts` module 載入時呼一次。 */
export function registerLoadedFirebaseAuth(auth: LoadedFirebaseAuth): void {
  loadedAuth = auth;
}

/** Firebase SDK 是否已在這個分頁載入(= 曾經發起過即時訂閱)。 */
export function isFirebaseAuthLoaded(): boolean {
  return loadedAuth !== null;
}

/**
 * `firebaseAuth.signOut()`(SDK 已載入才做;沒載入 = 沒有 Firebase session 可清)。
 * 失敗照原樣往上拋,由呼叫端決定要不要擋後續清理(兩個呼叫端都不擋)。
 */
export async function signOutLoadedFirebase(): Promise<void> {
  if (!loadedAuth) return;
  await loadedAuth.signOut();
}

/** reset `ensureFirebaseSignIn` 的 promise cache(SDK 已載入才有 cache)。 */
export function resetLoadedFirebaseSignIn(): void {
  loadedAuth?.resetSignInCache();
}

/** test 用:回到「SDK 沒載入過」的狀態。 */
export function resetLoadedFirebaseAuthForTest(): void {
  loadedAuth = null;
}
